import { UniversityStudentsQuery } from "./types";
import { StudentRow } from "./types";
import { getStatusTagColor } from "@/pages/education/universities/[universityId]/_utils";
import { buildPATStatus } from "./_utils/patStatus";
import {
  buildStudentColumns,
  buildProgramLevelColumn,
  buildNotesColumn,
  buildActionColumn,
  buildEmailColumn,
  buildInternshipMonthsColumn,
  buildPATStatusColumn,
  buildCohortColumn,
  type ExpansionState,
  type ExpandedColumn
} from "../../_shared/_utils";
import type { Action } from "@/components/MenuButton";
import { formatActiveProbationTooltip, type StudentActiveProbationSummary } from "./probations/_utils";

export const mapStudentsSimplified = (students: UniversityStudentsQuery["universityStudents"]): StudentRow[] => {
  if (!students?.nodes) return [];

  return students.nodes.map((node) => ({
    ...node,
    id: node.pgaId, // Keep PGA ID for table functionality
    studentId: node.id, // Add real student person UUID as separate field
    name: node.displayName,
    enrollStartDate: node.educationLatestPgmProgram.startDate,
    program: {
      level: node.educationLatestPgmProgram.level,
      version: node.educationLatestPgmProgram.curriculumVersion,
      completed: node.educationLatestPgmProgram.completed,
      programType: node.educationLatestPgmProgram.programType || ""
    },
    patStatus: buildPATStatus(node.prospectProgram?.playingAbilityTestStatus).value,
    programLevels: node.educationPgmProgramHistory.map((level, index) => ({
      id: index.toString(),
      level: level.level,
      levelCode: level.levelCode,
      startDate: level.startDate,
      endDate: level.endDate,
      completed: level.completed
    })),
    probationDate: null,
    graduationDate: null,
    totalInternshipMonths: node.totalInternshipMonths,
    universityCohort: node.universityCohort,
    cohortStatus: node.universityCohort?.name || null,
    playingAbilityTestStatus: node.prospectProgram?.playingAbilityTestStatus || null
  })) as unknown as StudentRow[];
};

type BuildColumnsProps = {
  isCompactLayout: boolean;
  hasCheckbox?: boolean;
  navigate: (to: string) => void;
  location: { pathname: string; search: string };
  universityId: string;
  handleViewStudent: (student: StudentRow) => void;
  handleOpenProbationDialog: (student: StudentRow) => void;
  handleOpenIndividualGraduationDialog: (student: StudentRow) => void;
  handleOpenNoteDialog: (student: StudentRow) => void;
  handleOpenEditCohortDialog: (student: StudentRow) => void;
  canEditCohort: boolean;
  setMenu: (menu: any) => void;
  actionId: (rowId: string) => string;
  studentNotesMap: Map<string, number>;
  studentProbationsMap: Map<string, StudentActiveProbationSummary>;
  expanded: ExpansionState;
  onExpand: (rowId: string, column: ExpandedColumn | null) => void;
};

export function buildColumns({
  isCompactLayout,
  hasCheckbox = true,
  navigate,
  location,
  universityId,
  handleViewStudent,
  handleOpenProbationDialog,
  handleOpenIndividualGraduationDialog,
  handleOpenNoteDialog,
  handleOpenEditCohortDialog,
  canEditCohort,
  setMenu,
  actionId,
  studentNotesMap,
  studentProbationsMap,
  expanded,
  onExpand
}: BuildColumnsProps) {
  return [
    ...buildStudentColumns<StudentRow>({
      isCompactLayout,
      hasCheckbox,
      sortableName: true
    }),

    buildEmailColumn(),
    buildCohortColumn<StudentRow>({
      getStatusTagColor,
      getCohortValue: (row) => row.universityCohort?.name || "",
      displayMode: "text",
      getActiveProbationStartDate: (row) => studentProbationsMap.get(row.studentId)?.displayStartDate ?? null,
      getActiveProbationTooltip: (row) => {
        const summary = studentProbationsMap.get(row.studentId);
        return summary ? formatActiveProbationTooltip(summary) : null;
      },
      sortable: true
    }),
    buildProgramLevelColumn<StudentRow>({
      getProgram: (row) => row.program,
      expanded,
      onExpand,
      expandedKey: "programLevels"
    }),
    buildInternshipMonthsColumn<StudentRow>(),
    buildPATStatusColumn<StudentRow>({
      getStatusTagColor,
      getPATStatus: (row) => row.patStatus,
      sortable: true
    }),
    buildNotesColumn<StudentRow>({
      studentNotesMap,
      expanded,
      onExpand,
      getStudentId: (row) => row.studentId
    }),

    buildActionColumn<StudentRow>({
      getActions: (row) => {
        const hasEnrollment = studentNotesMap.has(row.studentId);

        const actions: Action[] = [];

        if (hasEnrollment && canEditCohort) {
          actions.push({
            section: "Manage",
            text: "Edit Cohort",
            onClick: () => handleOpenEditCohortDialog(row)
          });
        }

        actions.push({
          section: "Manage",
          text: "Place Student on Probation",
          onClick: () => handleOpenProbationDialog(row)
        });

        actions.push({
          section: "Manage",
          text: "Mark Student as Graduated",
          onClick: () => handleOpenIndividualGraduationDialog(row)
        });

        actions.push({
          section: "Actions",
          text: "View Student",
          onClick: () => {
            const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
            navigate(`/education/universities/${universityId}/students/${row.pgaId}?returnUrl=${returnUrl}`);
          }
        });

        if (hasEnrollment) {
          actions.push({
            section: "Actions",
            text: "Add Note",
            onClick: () => handleOpenNoteDialog(row)
          });
        }

        return actions;
      },
      onViewClick: (row) => handleViewStudent(row),
      actionId,
      setMenu,
      viewButtonTitle: "View Student"
    })
  ];
}
